import { Maybe } from '@monetize/types/common';
import isNil from 'lodash/isNil';
import { useId } from 'react';
import {
  MCustomNumberInput,
  MFlex,
  MText,
} from '../../../../../components/Monetize';
import {
  SegmentedControl,
  SegmentedControlOption,
} from '../../../../../components/Monetize/SegmentedControl';

// The form stores "any frequency" as '' and a custom one as its month count;
// neither works as a radio value, so the control uses these instead
const ANY_FREQUENCY = 'any';
const CUSTOM_FREQUENCY = 'custom';

const FREQUENCY_OPTIONS: SegmentedControlOption<string>[] = [
  { label: 'Monthly', value: '1' },
  { label: 'Quarterly', value: '3' },
  { label: 'Semi-Annual', value: '6' },
  { label: 'Annual', value: '12' },
  { label: 'Any', value: ANY_FREQUENCY },
];

const CUSTOM_INPUT_PROPS = {
  'aria-label': 'Custom billing frequency in months',
  'data-testid': 'bulk-offering-custom-frequency-input',
};

interface BillingFrequencyProps {
  billingFrequency: string;
  customBillingFrequency: Maybe<number>;
  disabled?: boolean;
  /** Called with `''` for Any */
  onFrequencySelect: (billingFrequency: string) => void;
  /** Called with `null` when the custom value is cleared */
  onCustomFrequencyChange: (customBillingFrequency: number | null) => void;
}

export const BillingFrequency = ({
  billingFrequency,
  customBillingFrequency,
  disabled,
  onFrequencySelect,
  onCustomFrequencyChange,
}: BillingFrequencyProps) => {
  // Each quote group has its own bulk add, so its radios need their own name
  const radioGroupId = useId();

  // A custom value is not one of the options, so no segment shows as selected
  const segmentValue =
    !isNil(customBillingFrequency) || billingFrequency === '0'
      ? CUSTOM_FREQUENCY
      : billingFrequency || ANY_FREQUENCY;

  return (
    <MFlex align="center" columnGap={3} rowGap={2} wrap="wrap">
      <MText color="fg.muted">Frequency</MText>
      <SegmentedControl
        name={`bulk-offering-frequency-${radioGroupId}`}
        label="Billing frequency"
        options={FREQUENCY_OPTIONS}
        value={segmentValue}
        isDisabled={disabled}
        onChange={(value) =>
          onFrequencySelect(value === ANY_FREQUENCY ? '' : value)
        }
      />
      <MFlex align="center" gap={2}>
        <MText color="fg.muted">or every</MText>
        <MCustomNumberInput
          isDisabled={disabled}
          value={customBillingFrequency ?? ''}
          w="3.25rem"
          styleProps={CUSTOM_INPUT_PROPS}
          onChange={(valueAsString, valueAsNumber) =>
            onCustomFrequencyChange(valueAsString === '' ? null : valueAsNumber)
          }
        />
        <MText color="fg.muted">months</MText>
      </MFlex>
    </MFlex>
  );
};
